import React, { useEffect, useRef, useState } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { clipboardIcon } from "components/dashboard/charts/icons";
import { PlotDataEx } from "components/dashboard/charts/interfaces";
import {
  FormalSankeyConfig,
  SankeyDiagramProps,
} from "components/dashboard/charts/sankey-chart/sankey-types";
import {
  minimalNumber,
  timeSetWithConfig,
  validatedConfigIsCorrect,
} from "components/dashboard/charts/sankey-chart/sankey-utils";
import { transformDataToSankeyData } from "components/dashboard/charts/sankey-chart/TransformData";
import { copyToClipBoard } from "components/dashboard/dataset-info-search/utils";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";

import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import Plot from "react-plotly.js";
import { useResizeDetector } from "react-resize-detector";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { dweenColorway } from "utils/constants";

export default function Sankey (props: SankeyDiagramProps) {
  const plotRef = useRef(null);
  const { width, ref } = useResizeDetector({
    handleHeight: false,
    refreshOptions: { trailing: true },
  });
  const setStatus = useComponentStateCallback(props);

  const [date, setDate] = useState<Date>(
    timeSetWithConfig(props.referenceTime, {
      alignStartTo: props.alignStartTo,
      timeOffset: props.timeOffset,
      timeOffsetUnits: props.timeOffsetUnits,
    })
  );

  const [sankeyConfig, setSankeyConfig] = useState<FormalSankeyConfig>();
  const [sankeyData, setSankeyData] = useState<Partial<PlotDataEx>>();

  const editMode = useDashboardStore(state => state.isEditMode);

  const [error, setError] = useState<string>("");

  const {
    data,
    isLoading,
    error: responseError,
    refetch,
  } = useGetRecentData(
    props.graph.links.map(e => e.identifier),
    {
      refTime: date.toISOString(),
      usePromiseHandler: response => {
        const d = response.data as DatasetDataInfoResponse[];
        if (d) {
          return d.map(e => {
            if (e.data.length > 0 && e.data[0].value <= 0) {
              return { ...e, data: [{ ...e.data[0], value: minimalNumber }] };
            }

            return e;
          });
        }

        return response.data;
      },
    }
  );

  useEffect(() => {
    if (!isLoading && data) {
      const hasEmptyData = (data as DatasetDataInfoResponse[]).some(e => e.data.length === 0);

      if (hasEmptyData) {
        const errorMessages = (data as DatasetDataInfoResponse[])
          .filter(e => e.data.length === 0)
          .map(e => `${T("error.sankey.no-data-in-dataset")}: ${e.identifier}`);

        setError(errorMessages.join(", \n"));
        return;
      }

      if (validatedConfigIsCorrect(props.graph) !== "") {
        setError(
          `${T("error.sankey.no-node-in-config-called")}- ${validatedConfigIsCorrect(props.graph)}`
        );
        return;
      }

      setError("");
      setSankeyConfig(
        transformDataToSankeyData(
          props.graph,
          data,
          props.decimalPlaces,
          props.percentageDecimalPlaces
        )
      );
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (sankeyConfig) {
      setSankeyData({
        type: "sankey",
        domain: {
          x: [0, 1],
          y: [0, 1],
        },
        node: {
          color: props?.colorScheme,
          label: sankeyConfig?.nodeNames,
          x: props.overridePositions && props.nodesPosition ? props.nodesPosition.x : undefined,
          y: props.overridePositions && props.nodesPosition ? props.nodesPosition.y : undefined,
          hoverinfo: "none",
          thickness: 30,
          pad: props.nodesGap || 20,
        },
        link: {
          source: sankeyConfig?.links.source,
          target: sankeyConfig?.links.target,
          value: sankeyConfig?.links.value,
          label: sankeyConfig?.links.linkName,
          hovertemplate: `%{label}<br>%{customdata}<extra></extra>`,
          customdata: sankeyConfig?.links.customdata,
        },
        arrangement: "fixed",
      } as Partial<PlotDataEx>);
    }
  }, [sankeyConfig]);

  const copyNodesPosition = () => {
    // @ts-ignore
    const plotProps = plotRef.current?.props;
    const plotData = plotProps?.data;

    if (plotData && plotData.length > 0 && plotData[0].node) {
      const nodesConf = plotData[0].node;

      if (nodesConf.x && nodesConf.y) {
        const tempProps = { ...props };
        tempProps.nodesPosition = { x: nodesConf.x, y: nodesConf.y };
        const jsonString = JSON.stringify(tempProps, null, 2);

        copyToClipBoard(jsonString)
          .then(() => {
            easasAlert({
              summary: T("alert.success"),
              severity: "success",
              detail: T("alert.changed.config.copied"),
            });
          })
          .catch(err => {
            easasAlert({ summary: T("alert.error"), severity: "error", detail: err });
            console.error(err);
          });
      } else {
        easasAlert({
          summary: T("alert.warning"),
          severity: "warn",
          detail: T("alert.nodes-havent-changed-position"),
        });
        console.warn("No change in nodes position");
      }
    }
  };

  const renderError = () => {
    if (error) {
      return <pre>{error}</pre>;
    }

    if (responseError) {
      setStatus({ state: "error", error: responseError.message });
      return (
        <>
          {T("word.error")}: {responseError.message}
        </>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-content-center">
          <ProgressSpinner style={{ overflow: "hidden" }} />
        </div>
      );
    }

    if (!sankeyConfig) {
      return <>{T("message.wrong-config")}</>;
    }

    if (!sankeyData) {
      return <>{T("word.no-data")}</>;
    }

    return false;
  };

  return (
    <div ref={ref} className="flex flex-column gap-2">
      {!props.hideHeader && (
        <div className="flex gap-2 non-printable">
          <CalendarWrapper value={date} onChange={setDate} />
          {!props.reportMode && (
            <Button icon="pi pi-refresh" size="small" className="component-editor-button icon-blue" onClick={() => refetch()} />
          )}
        </div>
      )}
      {!renderError() && (
        <Plot
          ref={plotRef}
          data={[sankeyData!]}
          config={{
            displaylogo: false,
            modeBarButtonsToAdd: editMode
              ? [
                  {
                    title: T("action.copy-nodes-position"),
                    name: "Copy Nodes Position",
                    icon: clipboardIcon,
                    click: copyNodesPosition,
                  },
                ]
              : [],
          }}
          layout={{
            width,
            height: props.height || 400,
            colorway: dweenColorway,
            margin: {
              l: 0,
              t: 0,
              r: 0,
              b: 20,
            },
          }}
          onInitialized={() => setStatus({ state: "finished" })}
        />
      )}
    </div>
  );
};
